import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import { UserMode } from '@/components/context/ModeContext';
import { UseNav } from '../../context/NavContext';
import useBodyClass from '@/components/utils/useBodyClass';
import Links from '../../molecules/Links';
import NavBodyOpen from './NavBodyOpen';
import SocialLinks from './SocialLinks';
import { useMediaQuery } from '@/components/utils/useMediaQuery';
import styles from './styles.module.scss';
import clsx from 'clsx';

export default function Nav() {
  const loc = useLocation();

  const { darkMode, setDarkMode } = UserMode();
  const { isActive, setIsActive } = UseNav();
  const [hidden, setHidden] = useState(false);
  const [navAnimation, setNavAnimation] = useState(() =>
    loc.pathname === '/galleries' ? 'visible' : 'hidden'
  );

  useBodyClass('overflow-hidden fixed h-screen', isActive);
  const mobileNav = useMediaQuery('(min-width: 850px)');

  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, 'change', (latest) => {
    const previous = scrollY.getPrevious();

    if (latest > previous && latest > 150) {
      setHidden(true);
    } else {
      setHidden(false);
    }
  });

  useEffect(() => {
    const animLogic = () => {
      if (loc.pathname !== '/galleries') {
        return hidden ? 'hidden' : 'visible';
      } else {
        return 'visible';
      }
    };

    setNavAnimation(animLogic());
  }, [loc.pathname, hidden]);

  const toggleMode = () => {
    setDarkMode((prevMode) => !prevMode);
  };

  return (
    <>
      <motion.nav
        variants={{
          visible: { y: 0 },
          hidden: { y: -100, background: '#46464638' },
        }}
        transition={{ duration: 0.66, ease: 'easeInOut' }}
        animate={navAnimation}
        className={clsx(
          'flex justify-between max-md2:justify-end fixed w-screen pb-4 pt-6 font-bold max-xs:px-4 xs-lg:px-8 lg-xl:px-16 xl:px-[14%]',
          isActive ? 'z-[999999]' : 'z-[999]'
        )}
      >
        <motion.ul className="flex gap-2 max-md2:hidden items-center">
          <SocialLinks />
        </motion.ul>

        <div className="flex">
          {mobileNav && <Links />}
          {!mobileNav && (
            <button
              onClick={() => setIsActive(!isActive)}
              className="flex items-center"
            >
              <div
                className={`${styles.burger} ${
                  isActive ? styles.burgerActive : ''
                } 
                ${
                  darkMode
                    ? 'before:bg-white after:bg-white'
                    : 'before:bg-black after:bg-black'
                }`}
              ></div>
            </button>
          )}
          <button
            onClick={() => toggleMode()}
            className="bg-none border-none ml-4"
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
        </div>
      </motion.nav>
      <motion.div
        variants={{
          hidden: { height: 0 },
          visible: { height: '100svh' },
        }}
        transition={{ duration: 0.66, ease: 'easeInOut' }}
        animate={isActive ? 'visible' : 'hidden'}
        initial="hidden"
        className={clsx(
          'w-screen fixed z-[999] place-content-center',
          darkMode ? 'bg-black' : 'bg-white'
        )}
      >
        <NavBodyOpen />
      </motion.div>
    </>
  );
}
